'use client';

import { useMemo } from 'react';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { DEEP_SECURITY_TEST_CASE_SCAN_ENABLED_FLAG } from '@terra/shared-contracts-feature-flags';
import {
  APPLICATION_PIPELINES,
  toTestingInstructionNote,
  toTestingInstructionNoteBody,
  type TestingInstructionNoteInput,
  type TestingInstructionsTarget,
} from '@terra/ui-design-system';
import { terraProxyApiClient } from '../../../clients/testerClient';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import {
  useTestingControlsApplicationScope,
  type TestingControlsApplicationScope,
} from './useTestingControlsApplicationScope';

const APPLICATION_INSTRUCTIONS_QUERY_KEY = 'application-testing-instructions';

const getApplicationInstructionsQueryKey = (applicationId: string) =>
  [APPLICATION_INSTRUCTIONS_QUERY_KEY, applicationId] as const;

export const applicationInstructionsQueryOptions = (applicationId: string) =>
  queryOptions({
    queryKey: getApplicationInstructionsQueryKey(applicationId),
    queryFn: () => terraProxyApiClient.getApplicationNotes(applicationId),
  });

type SaveNoteInput = {
  noteId?: string;
  note: TestingInstructionNoteInput;
};

const saveApplicationNote = ({
  applicationId,
  noteId,
  note,
}: SaveNoteInput & { applicationId: string }) => {
  const body = toTestingInstructionNoteBody(note);

  return noteId
    ? terraProxyApiClient.updateApplicationNote(applicationId, noteId, body)
    : terraProxyApiClient.createApplicationNote(applicationId, body);
};

const createApplicationTestingInstructionsTarget = ({
  applicationId,
  targetKind,
  isDeepSecurityScanEnabled,
  queryClient,
}: TestingControlsApplicationScope & {
  isDeepSecurityScanEnabled: boolean;
  queryClient: QueryClient;
}): TestingInstructionsTarget => {
  const invalidateNotes = () => {
    void queryClient.invalidateQueries({
      queryKey: getApplicationInstructionsQueryKey(applicationId),
    });
  };

  return {
    targetId: applicationId,
    targetKind,
    availablePipelines: APPLICATION_PIPELINES,
    isDeepSecurityScanEnabled,
    persistMode: 'immediate',
    invalidateNotes,
    useNotes: () => {
      const { data, isLoading } = useQuery(
        applicationInstructionsQueryOptions(applicationId),
      );

      return {
        notes: (data?.notes ?? []).map(toTestingInstructionNote),
        isLoading,
      };
    },
    useSaveNote: () => {
      const mutation = useMutation({
        mutationFn: (input: SaveNoteInput) =>
          saveApplicationNote({ ...input, applicationId }),
      });

      return {
        isPending: mutation.isPending,
        saveAsync: async (input) => {
          const result = await mutation.mutateAsync(input);

          if (!result.success || !result.data) {
            return {
              success: false,
              message: result.message,
              validationErrors: result.validationErrors,
            };
          }

          return {
            success: true,
            data: toTestingInstructionNote({
              ...result.data,
              category: result.data.category ?? null,
            }),
            message: result.message,
          };
        },
      };
    },
    useDeleteNote: () => {
      const mutation = useMutation({
        mutationFn: (noteId: string) =>
          terraProxyApiClient.deleteApplicationNote(applicationId, noteId),
        onSuccess: invalidateNotes,
      });

      return {
        isPending: mutation.isPending,
        deleteAsync: async (noteId) => {
          await mutation.mutateAsync(noteId);
        },
      };
    },
  };
};

export const useApplicationTestingInstructionsTarget =
  (): TestingInstructionsTarget | null => {
    const queryClient = useQueryClient();
    const scope = useTestingControlsApplicationScope();
    const { isFeatureFlagEnabled: isDeepSecurityScanEnabled } = useFeatureFlags(
      DEEP_SECURITY_TEST_CASE_SCAN_ENABLED_FLAG,
    );

    return useMemo(
      () =>
        scope
          ? createApplicationTestingInstructionsTarget({
              ...scope,
              isDeepSecurityScanEnabled,
              queryClient,
            })
          : null,
      [isDeepSecurityScanEnabled, queryClient, scope],
    );
  };
